import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ReactNode, Ref } from "react";

import { cn } from "../lib/cn";
import {
  BUSY_CONTROL,
  FOCUS_RING,
  HIT_SLOP,
  REDUCED_MOTION_SAFE,
} from "../lib/focus";
import { Spinner } from "../Spinner/Spinner";

/**
 * Exactly one `primary` button per screen. Everything
 * else on that screen is `secondary`, `outline`, `ghost`, or `link`.
 * `destructive` is its own visual class and never sits next to a primary.
 */
export const buttonVariants = cva(
  cn(
    // Layout. `relative` is required by the compact size's hit-slop overlay.
    "relative inline-flex shrink-0 select-none items-center justify-center gap-2",
    "rounded-sm text-body-strong whitespace-nowrap",
    // A press-down is essential feedback, so it survives reduced motion; the
    // color transition does not.
    "transition-colors duration-100 ease-standard active:scale-[0.98]",
    REDUCED_MOTION_SAFE,
    FOCUS_RING,
    // One disabled treatment for every variant, so a disabled outline button
    // and a disabled primary read the same. The `aria-disabled:` half is the
    // same treatment for a control that is busy rather than disabled, and the
    // rule is in `BUSY_CONTROL` in `../lib/focus`.
    "disabled:pointer-events-none disabled:border-disabled-border disabled:bg-disabled-bg",
    "disabled:text-disabled-text disabled:shadow-none disabled:active:scale-100",
    "aria-disabled:pointer-events-none aria-disabled:border-disabled-border aria-disabled:bg-disabled-bg",
    "aria-disabled:shadow-none aria-disabled:active:scale-100",
    BUSY_CONTROL,
  ),
  {
    variants: {
      variant: {
        primary:
          "border border-primary bg-primary-solid text-on-primary hover:bg-primary-solid-hover active:bg-primary-solid-active",
        secondary:
          "border border-line bg-surface text-ink hover:bg-surface-sunken active:bg-disabled-bg",
        outline:
          "border-2 border-primary bg-transparent text-primary hover:bg-primary-tint active:bg-primary-tint",
        ghost:
          "bg-transparent text-primary hover:bg-primary-tint active:bg-primary-tint",
        destructive:
          "bg-error-solid text-on-error hover:bg-error-hover active:bg-error-hover",
        link: "bg-transparent text-primary underline underline-offset-4 hover:text-primary-hover",
      },
      size: {
        /** The platform default. 48px tall, comfortably over the 44px floor. */
        md: "h-control-height min-w-touch-target px-4",
        /** Compact rows. 36px tall, padded out to a 44px tap target by hit-slop. */
        sm: cn(
          "h-control-height-sm min-w-touch-target px-3 text-caption",
          HIT_SLOP,
        ),
        /** Icon-only. Square at the default control height, so 48x48. */
        icon: "h-control-height w-control-height p-0",
      },
      fullWidth: {
        // The screen's main CTA is full width on a phone.
        true: "w-full",
        false: "",
      },
      /** For a stacked label (a heading plus a caption), which wraps and grows instead of overflowing. Off by default: a single line button label should never wrap. */
      multiline: {
        true: "h-auto min-h-control-height py-2 whitespace-normal",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
      multiline: false,
    },
  },
);

export type typeButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "color"
> &
  VariantProps<typeof buttonVariants> & {
    /**
     * Render the child element instead of a `<button>`, keeping every style.
     * For a button that is really a link. Not compatible with `loading`, which
     * needs to inject a spinner alongside the label.
     */
    asChild?: boolean;
    /**
     * The spinner belongs to the control that was tapped. Setting this marks
     * the button `aria-busy`, refuses presses, and keeps the label visible so
     * the button does not change width mid-action. It does NOT set the
     * `disabled` attribute, so the button stays focusable. The rule and the
     * reason are in `BUSY_CONTROL` in `../lib/focus`.
     */
    loading?: boolean;
    /** Announced to screen readers while `loading`. Verb-first, like the label. */
    loadingLabel?: string;
    iconLeading?: ReactNode;
    iconTrailing?: ReactNode;
    ref?: Ref<HTMLButtonElement>;
  };

/**
 * The platform's button.
 *
 * Labels are verbs a fan would say out loud — "Submit picks," "Join pool" —
 * never a bare "OK" or "Submit."
 */
export const Button = ({
  asChild = false,
  className,
  variant,
  size,
  fullWidth,
  multiline,
  loading = false,
  loadingLabel = "Working",
  iconLeading,
  iconTrailing,
  disabled,
  onClick,
  children,
  type = "button",
  ref,
  ...rest
}: typeButtonProps) => {
  const classes = cn(
    buttonVariants({ variant, size, fullWidth, multiline }),
    className,
  );

  if (asChild) {
    return (
      <Slot className={classes} onClick={onClick} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      // A `type` default of "button" is deliberate: an unlabeled button inside
      // a form otherwise submits it, which is how a "Cancel" ends up saving.
      type={type}
      className={classes}
      disabled={disabled === true}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      data-loading={loading ? "true" : undefined}
      data-variant={variant ?? "primary"}
      onClick={(event) => {
        // `aria-disabled` is advice to assistive technology, not a block: the
        // press still arrives, so this early return is what refuses it.
        // A `type="submit"` button that is not `disabled` still submits its
        // form unless this click is canceled. Implicit submission (Enter in a)
        // field) activates this same click path.
        if (loading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {loading ? (
        <Spinner className="h-icon-md w-icon-md" data-testid="button-spinner" />
      ) : (
        iconLeading
      )}
      {children}
      {loading ? <span className="sr-only">{loadingLabel}</span> : iconTrailing}
    </button>
  );
};
